// cspell:ignore pcheebum

import { Currency, isCurrency } from "@redotech/money/currencies";

import { useRequiredContext } from "@redotech/react-util/context";
import { useEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { StyleShadowRoot } from "@redotech/react-util/style";
import { CartProductType } from "@redotech/redo-model/cart-product-types";
import { getAllReturnEligibleCountries } from "@redotech/redo-model/coverage-calculator/country-check";
import { isAttachmentStrategyUsingCheckoutButtons } from "@redotech/redo-model/returns/attachment-strategy";

import {
  AttachmentStrategy,
  isAttachmentStrategyCheckoutExtensionEnabled,
  singleCartToggleAttachmentStrategies,
} from "@redotech/redo-model/returns/attachment-strategy";

import {
  CoverageInfo,
  InsertionPosition,
  PlacementType,
  WidgetAutocheck,
  WidgetConfig,
  WidgetElementType,
  WidgetFees,
} from "@redotech/redo-model/widget";

import { getCalculatedCoveragePrice } from "@redotech/redo-shopify-utils/price-calculations";
import { getMatchingRedoSellingPlanId } from "@redotech/redo-shopify-utils/selling-plan-utils";
import { ModalRootContext } from "@redotech/redo-web/modal-root";

import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";

import type {
  ShopifyCart,
  ShopifyProduct,
  ShopifyProducts,
} from "@redotech/shopify-client/ajax";

import {
  DeliveryType,
  ExtensionShopifyClient,
  ShopifyAjaxClient,
  ShopifyCartItem,
  ShopifyClientType,
} from "@redotech/shopify-client/ajax";

import { nativeRandom, randomHexString } from "@redotech/util/random";
import { assertNever } from "@redotech/util/type";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { useDebounce } from "usehooks-ts";
import { RedoToggleElement } from "./cart-app";
import { RedoCheckbox } from "./checkbox";
import type { RedoCheckboxElement } from "./checkbox-app";
import { BigcommerceServerApiClient } from "./client/bigcommerce-server-api-client";
import { BigcommerceStorefrontClient } from "./client/bigcommerce-storefront";
import { ShopifyExtensionClientContext } from "./client/client-context";
import { CommercecloudStorefrontClient } from "./client/commercecloud-storefront";
import { getSellingPlan } from "./client/shopify-extension-client";
import { StorefrontClient } from "./client/storefront";
import { RedoListenedDomEvent } from "./dom-events";
import { itemValidForReturnsFrontend } from "./eligibility/returns-coverage-eligibility";
import { useLocationManager } from "./location";
import { log } from "./monitoring/log";
import { reportTiming } from "./monitoring/timing";
import { TAPCART_SELECTOR, addCheckbox } from "./product-page";
import { renderCart } from "./render-cart";
import { getClassicWidgetTheme } from "./render-elements/classic-widget-theme";
import { RedoRoot } from "./root";

import {
  DEFAULT_TOGGLE_CLICK_SELECTOR,
  DEFAULT_TOGGLE_PLACEMENT,
  DEFAULT_TOGGLE_SELECTOR,
  getSubmitButtonSelector,
} from "./selectors";

import {
  ExtensionShopifyClientContext,
  isOnProductPage,
  shopifyProductHandle,
} from "./shopify";

import STYLE_MANAGER from "./style-manager";
import { DoubleToggleManager } from "./sub-managers/double-toggle-manager";
import { SingleToggleManager } from "./sub-managers/single-toggle-manager";
import { WidgetManager } from "./sub-managers/widget-manager";

import {
  REDO_DISABLE_TAGS,
  RedoProductToSell,
  getCartShadowRoot,
  isRedoItem,
  queryDocumentAndShadowRoot,
} from "./utils";

import { useBuyerIdentity, useCart } from "./utils/cart";
import { applyCartCountModificationsWithRetries } from "./utils/cart-count-modifications";
import { useBFCacheListener } from "./utils/checkout-buttons/on-page-refresh-hooks";

import {
  compareCoverageInfos,
  getCoverageInfoFromCoverageCalculators,
} from "./utils/coverage-calculations";

import {
  determineRedoProduct,
  determineRedoProductType,
  getCorrectlyPricedVariant,
  getVariantAndProduct,
} from "./utils/determine-redo-product";

import {
  getPlacedRedoToggle,
  getReactElementFromPlacement,
  widgetSortOrder,
} from "./utils/element-placement";

import { useHideRedoProductAndCheckoutButtons } from "./utils/hide-elements";
import { usePageChange } from "./utils/page-change";

import {
  EnsurePromiseAction,
  PROTECTION_TOGGLE_STATE_KEY,
  REDO_REMOVED_REDO,
  RETURN_TOGGLE_STATE_KEY,
  SELLING_PLANS_ID,
  SINGLE_TOGGLE_STATE_KEY,
} from "./utils/signals";

import { useCoverageCheckoutButtonClicked } from "./utils/checkout-buttons/use-coverage-checkout-button-clicked";
import {
  extractProductFormData,
  useBuyNowCoverage,
} from "./utils/use-buy-now-coverage";

export function initialize(
  storefrontClient: ExtensionShopifyClient,
  shopifyStorefrontGraphqlClient: StorefrontClient | undefined,
  autocheck: WidgetAutocheck,
  cartEnabled: boolean,
  fees: WidgetFees,
  shopifyProducts: ShopifyProducts,
  shouldUseStorefrontGraphqlClientForRIC: boolean,
) {
  if (document.querySelector("#redo-container") || globalThis.blockRedo) {
    return;
  }
  RedoToggleElement.register();
  const redoContainer = document.createElement("div");
  redoContainer.id = "redo-container";
  document.head.append(redoContainer);
  const root = createRoot(redoContainer);
  root.render(
    <RedoRoot
      extensionShopifyClient={storefrontClient}
      storeUrl={
        globalThis.redoStorefront?.storeUrl ??
        globalThis.Shopify?.shop ??
        globalThis.Shopify?.Checkout?.apiHost
      }
      widgetId={globalThis.redoWidgetId}
    >
      <RedoManager
        autocheck={autocheck}
        cartEnabled={cartEnabled}
        fees={fees}
        shopifyProducts={shopifyProducts}
        shopifyStorefrontGraphqlClient={shopifyStorefrontGraphqlClient}
        shouldUseStorefrontGraphqlClientForRIC={
          shouldUseStorefrontGraphqlClientForRIC
        }
      />
    </RedoRoot>,
  );

  globalThis.removeRedo = () => {
    log("Removing redo");
    root.unmount();
    redoContainer.remove();
  };
}

interface RedoManagerProps {
  autocheck: WidgetAutocheck;
  cartEnabled: boolean;
  fees: WidgetFees;
  shopifyProducts: ShopifyProducts;
  shopifyStorefrontGraphqlClient: StorefrontClient | undefined;
  shouldUseStorefrontGraphqlClientForRIC: boolean;
}

type AsyncEventHandler = (
  event: React.MouseEvent | React.TouchEvent,
) => Promise<void>;

const RedoManager = memo(function RedoManager({
  autocheck,
  cartEnabled,
  fees,
  shopifyProducts,
  shopifyStorefrontGraphqlClient,
  shouldUseStorefrontGraphqlClientForRIC,
}: RedoManagerProps) {
  useEffect(() => {
    log("RedoManager mounted");
    return () => {
      log("RedoManager unmounted");
    };
  }, []);

  const locationManager = useLocationManager();
  const storefrontClient = useRequiredContext(ExtensionShopifyClientContext);
  const onTapcart = storefrontClient.type === ShopifyClientType.TAPCART;
  const redoShopifyExtensionClient = useRequiredContext(
    ShopifyExtensionClientContext,
  );
  const merchantPaid = autocheck.paidModel === "merchant_paid";

  const [toggles, setToggles] = useState<
    { redoToggleElements: RedoToggleElement[]; widgetConfig?: WidgetConfig }[]
  >([]);

  type pdpProductLoad =
    | {
        returnsValid: boolean;
        protectionValid: boolean;
        finalSaleReturnsValid: boolean;
        isValidProduct: boolean;
        product: ShopifyProduct | undefined;
      }
    | undefined;

  const buyNowRedoProduct = useRef<string | null>(null);
  const onBuyNowCoverageRef = useRef<AsyncEventHandler | null>(null);
  const pdpProductLoadRef = useRef<pdpProductLoad>(undefined);

  const setOnBuyNowCoverage = (fn: AsyncEventHandler | null) => {
    onBuyNowCoverageRef.current = fn;
  };

  const setBuyNowRedoProduct = (productId: string | null) => {
    buyNowRedoProduct.current = productId;
  };

  useBuyNowCoverage({
    shouldInterceptBuyNow: autocheck.useBuyNowCoverage,
    onClickCoverage: onBuyNowCoverageRef.current,
    storefrontClient: shopifyStorefrontGraphqlClient,
    buyNowRedoProduct: buyNowRedoProduct.current,
    buyNowCustomCss: autocheck.buyNow?.customCss,
  });

  // Initialize selling plan for redo state.
  useEffect(() => {
    if (!getLocalStorageWithExpiry(SELLING_PLANS_ID)) {
      setLocalStorageWithExpiry(SELLING_PLANS_ID, {});
    }
  }, []);

  const countryLoad = useLoad(
    async (signal) => {
      if (
        !autocheck.countries?.length &&
        !autocheck.countriesPackageProtection?.length
      ) {
        return null;
      }
      const result =
        (!autocheck.forceIPAddressLocation && globalThis.Shopify?.country) ||
        (await locationManager.getCountry(signal));
      return result;
    },
    [autocheck, locationManager],
  );

  const returnCountries = getAllReturnEligibleCountries(
    autocheck.countries,
    autocheck.internationalReturnsPricing,
  );
  const countryLoadPending =
    (countryLoad.value === undefined && !countryLoad.error) ||
    countryLoad.pending;
  const validCountryReturns =
    countryLoad.value === null ||
    returnCountries.includes(countryLoad.value!) ||
    returnCountries.length == 0;

  const validCountryFinalSale =
    countryLoad.value === null ||
    autocheck.countriesFinalSaleReturns?.includes(countryLoad.value!) ||
    autocheck.countriesFinalSaleReturns?.length === 0;

  const validCountryPackageProtection =
    countryLoad.value === null ||
    autocheck.countriesPackageProtection.includes(countryLoad.value!) ||
    autocheck.countriesPackageProtection.length === 0;

  const redoLock = useRef(false);
  const checkboxState = useRef(autocheck.redoAutoCheck);
  const [checkedCart, setCheckedCart] = useState<ShopifyCart | undefined>(
    undefined,
  );
  const [redoInCart, setInCart] = useState<boolean | undefined>(undefined);
  const [singleToggleState, setSingleToggleVal] = useState(false);
  const [returnToggleState, setReturnToggleVal] = useState(false);
  const [protectionToggleState, setProtectionToggleVal] = useState(false);
  const [coverageInfo, setCoverageInfo] = useState<CoverageInfo | undefined>(
    undefined,
  );
  const [checkbox, setCheckbox] = useState<RedoCheckboxElement | null>(null);
  const [cartCurrency, setCartCurrency] = useState<Currency>(Currency.USD);
  const [cartRefreshSignal, setCartRefresh] = useState(Symbol());
  const { coverageCheckoutButtonClicked, setCoverageCheckoutButtonClicked } =
    useCoverageCheckoutButtonClicked();
  const [cartSellingPlans, setCartSellingPlans] = useState<any[]>([]);
  const [pdpSelectedVariant, setPdpSelectedVariant] = useState<{
    variantId: number | string;
    quantity: number;
  }>({ variantId: 0, quantity: 1 });

  const [toggleRefreshSignal, setToggleRefresh] = useState(Symbol());
  const [checkboxRefresh, setCheckboxRefresh] = useState(Symbol());
  const cartRefresh = useDebounce(cartRefreshSignal, 300);
  const toggleRefresh = useDebounce(toggleRefreshSignal, 300);
  const cart = useCart(cartRefresh);
  const buyerIdentityLoad = useBuyerIdentity(shopifyStorefrontGraphqlClient);
  const cartItems = cart?.items;
  const cartChecked = !!cart && cart === checkedCart;
  const cartCheckedOnce = !!checkedCart;
  const shouldShowBuyNowElements =
    autocheck.useBuyNowCoverage &&
    !cart?.items.length &&
    pdpProductLoadRef.current;

  useBFCacheListener(setCoverageCheckoutButtonClicked);
  useHideRedoProductAndCheckoutButtons(autocheck, fees, toggles, cart);

  const checkboxLabelOnly =
    cartEnabled ||
    autocheck.checkoutExtensionEnabled ||
    isAttachmentStrategyCheckoutExtensionEnabled[
      autocheck.attachmentStrategy
    ] ||
    merchantPaid;
  const hasTwoOptions =
    autocheck.attachmentStrategy === AttachmentStrategy.DOUBLE_TOGGLE ||
    autocheck.attachmentStrategy === AttachmentStrategy.DROP_DOWN;

  const invalidReturnProductTags = useMemo(
    () =>
      [...(autocheck.finalSaleTags || []), ...REDO_DISABLE_TAGS].map((s) =>
        s.toLowerCase(),
      ),
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const invalidProtectionProductTags = useMemo(
    () =>
      autocheck.protectionExcludeTags
        ? autocheck.protectionExcludeTags.map((s) => s.toLowerCase())
        : [],
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const mainRedoProduct = shopifyProducts.mainShopifyProduct;
  const returnRedoProduct = shopifyProducts.returnShopifyProduct;
  const packageRedoProduct = shopifyProducts.packageShopifyProduct;
  const bothRedoProduct = shopifyProducts.bothShopifyProduct;

  const productTypeMap = new Map<number | string, string>();

  if (mainRedoProduct && fees) {
    const productTypes = [
      { product: mainRedoProduct, type: fees.mainType },
      { product: returnRedoProduct, type: CartProductType.Return },
      { product: packageRedoProduct, type: CartProductType.PackageProtection },
      { product: bothRedoProduct, type: CartProductType.Both },
    ];
    for (const p of productTypes) {
      if (p.product) productTypeMap.set(p.product.id, p.type);
    }
  }

  const productIdToSellingPlanId = useLoad(async () => {
    if (!cartSellingPlans?.length || !fees) {
      return {};
    }

    const feesItems = [
      fees.returnProduct,
      fees.packageProduct,
      fees.bothProduct,
    ];

    const newMapping: Record<string, number> = {};

    for (const feesItem of feesItems) {
      if (feesItem?.sellingPlanGroups) {
        const sellingPlanId = await getMatchingRedoSellingPlanId(
          feesItem,
          cartSellingPlans,
        );

        if (sellingPlanId) {
          newMapping[String(feesItem.productId)] = sellingPlanId;
        }
      }
    }

    log({ sellingPlanIDs: newMapping });
    return newMapping;
  }, [cartSellingPlans, fees]);

  const redoProductToSell: RedoProductToSell | undefined = useMemo(
    () => {
      if (!fees || !mainRedoProduct || !coverageInfo) {
        return undefined;
      }

      const productType =
        determineRedoProductType({
          fees,
          autocheck,
          coverageInfo,
          toggleStates: { returnToggleState, protectionToggleState },
        }) ?? CartProductType.Return;
      let calculatedCoveragePrice: number = 0;

      const { variant, product } = getVariantAndProduct({
        coverageInfo,
        mainRedoProduct,
        returnRedoProduct,
        packageRedoProduct,
        bothRedoProduct,
        fees,
        autocheck,
        productTypeMap,
        toggleStates: { returnToggleState, protectionToggleState },
        country: countryLoad.value ?? undefined,
      });

      if (productType && product) {
        switch (productType) {
          case CartProductType.Return:
            calculatedCoveragePrice = coverageInfo.returns.price;
            break;
          case CartProductType.Both:
            calculatedCoveragePrice = coverageInfo.both.price;
            break;
          case CartProductType.PackageProtection:
            calculatedCoveragePrice = coverageInfo.packageProtection.price;
            break;
        }
      } else {
        log("Could not calculate coverage price for fee");
      }

      const useCustomFeeInsteadOfProduct =
        autocheck.attachmentStrategy ===
        AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE;

      if (useCustomFeeInsteadOfProduct) {
        const result = {
          variantId: 0,
          productId: 0,
          price: 0,
          rawCalculatedCoveragePrice: onTapcart
            ? calculatedCoveragePrice
            : calculatedCoveragePrice / 100,
          productType,
        };
        log(
          `Redo product to sell is ${result.productType} custom fee of price ${result.rawCalculatedCoveragePrice}`,
          result,
        );
        return result;
      } else {
        const result = {
          variantId: variant.id,
          productId: product.id,
          price: onTapcart ? variant.price : variant.price / 100,
          rawCalculatedCoveragePrice: onTapcart
            ? calculatedCoveragePrice
            : calculatedCoveragePrice / 100,
          productType,
        };
        log(
          `Redo product to sell is the ${result.productType} product with variant ${result.variantId} and price ${result.price}`,
        );
        return result;
      }
    },
    // FIXME can't have exhaustive deps because productTypeMap is set every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fees,
      mainRedoProduct,
      cartChecked,
      returnRedoProduct,
      packageRedoProduct,
      bothRedoProduct,
      autocheck,
      coverageInfo,
      returnToggleState,
      protectionToggleState,
      countryLoad.value,
      onTapcart,
    ],
  );

  // Finds expected variant every time anything changes.
  const {
    variantId: expectedVariantID,
    productId: expectedProductId,
    feeAction: ensurePromiseFeeAction,
  } = useMemo(() => {
    const SKIP_ENSURE_PROMISE = {
      variantId: null,
      productId: null,
      price: null,
      calculatedCoveragePrice: null,
      productType: null,
      feeAction: EnsurePromiseAction.IGNORE,
    };
    const REDO_SHOULD_NOT_BE_IN_CART = {
      variantId: 0,
      productId: 0,
      price: 0,
      calculatedCoveragePrice: 0,
      productType: null,
      feeAction: EnsurePromiseAction.REMOVE,
    };

    if (!fees || !mainRedoProduct || !cartChecked || !redoProductToSell) {
      return SKIP_ENSURE_PROMISE;
    }

    if (autocheck.attachmentStrategy === AttachmentStrategy.CHECKBOX) {
      return {
        variantId: redoProductToSell.variantId,
        productId: redoProductToSell.productId,
        feeAction: EnsurePromiseAction.IGNORE,
      };
    }

    if (checkboxLabelOnly || !coverageCheckoutButtonClicked) {
      if (
        fees.splitProducts &&
        autocheck.packageProtection.coverageEnabled &&
        hasTwoOptions
      ) {
        if (!returnToggleState && !protectionToggleState) {
          return REDO_SHOULD_NOT_BE_IN_CART;
        }
      } else if (!singleToggleState) {
        return REDO_SHOULD_NOT_BE_IN_CART;
      }
    }

    const isAnyToggleEnabled =
      singleToggleState || returnToggleState || protectionToggleState;

    const useCustomFeeInsteadOfProduct =
      autocheck.attachmentStrategy ===
      AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE;

    return {
      variantId: useCustomFeeInsteadOfProduct ? 0 : redoProductToSell.variantId,
      productId: useCustomFeeInsteadOfProduct ? 0 : redoProductToSell.productId,
      feeAction:
        useCustomFeeInsteadOfProduct && isAnyToggleEnabled
          ? EnsurePromiseAction.ADD
          : EnsurePromiseAction.REMOVE,
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fees,
    mainRedoProduct,
    returnRedoProduct,
    packageRedoProduct,
    bothRedoProduct,
    cartItems,
    redoInCart,
    coverageInfo,
    redoProductToSell,
    returnToggleState,
    protectionToggleState,
    singleToggleState,
    coverageCheckoutButtonClicked,
  ]);

  const renderToggles = useHandler((placementTypeToRender?: PlacementType) => {
    if (!cartEnabled || merchantPaid) {
      return;
    }
    log("Rendering toggles");
    let toggleLocations: {
      elements: Element[];
      widgetConfig?: WidgetConfig;
    }[] = [];
    if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
      autocheck.widgetConfigs?.length &&
      !onTapcart
    ) {
      // Determine if we should filter to PDP-only widgets
      const widgetConfigsToRender =
        placementTypeToRender === PlacementType.PDP
          ? autocheck.widgetConfigs.filter(
              (widgetConfig) =>
                widgetConfig.placementType === placementTypeToRender,
            )
          : autocheck.widgetConfigs;

      for (const widgetConfig of widgetConfigsToRender) {
        toggleLocations.push({
          elements: getReactElementFromPlacement({ widgetConfig, autocheck }),
          widgetConfig,
        });
      }
    } else if (autocheck.cartToggle.cartToggleShadowRootSelector) {
      const shadowRoot = getCartShadowRoot(autocheck);
      let shadowRootLocations: Element[] = [];
      shadowRootLocations = shadowRoot
        ? Array.from(
            shadowRoot.querySelectorAll(
              onTapcart
                ? TAPCART_SELECTOR
                : autocheck.cartToggle.cartToggleShadowRootSelector ||
                    DEFAULT_TOGGLE_SELECTOR,
            ),
          )
        : [];

      toggleLocations = [
        {
          elements: [
            ...Array.from(
              document.querySelectorAll(
                onTapcart
                  ? TAPCART_SELECTOR
                  : autocheck.cartToggle.cartToggleSelector ||
                      DEFAULT_TOGGLE_SELECTOR,
              ),
            ),
            ...shadowRootLocations,
          ],
        },
      ];
    } else {
      const docOrShadowRoot = getCartShadowRoot(autocheck) ?? document;
      toggleLocations = [
        {
          elements: Array.from(
            docOrShadowRoot.querySelectorAll(
              onTapcart
                ? TAPCART_SELECTOR
                : autocheck.cartToggle.cartToggleSelector ||
                    DEFAULT_TOGGLE_SELECTOR,
            ),
          ),
        },
      ];
    }

    const toggles: {
      redoToggleElements: RedoToggleElement[];
      widgetConfig?: WidgetConfig;
    }[] = [];

    const groupElementTogether =
      autocheck.groupNearbyToggleElementsTogether ?? true;
    if (!groupElementTogether) {
      // sort the elements so they render in the correct order
      toggleLocations.sort(
        (a, b) =>
          widgetSortOrder(a.widgetConfig) - widgetSortOrder(b.widgetConfig),
      );
    }

    for (const { elements, widgetConfig } of toggleLocations) {
      log("Rendering location.");
      toggles.push({
        redoToggleElements: elements.map((container): RedoToggleElement => {
          let toggle: RedoToggleElement | undefined;
          const insertionPosition: InsertionPosition =
            widgetConfig?.insertionPosition ||
            autocheck.cartToggle.cartTogglePlacement ||
            DEFAULT_TOGGLE_PLACEMENT;

          switch (insertionPosition) {
            case InsertionPosition.BEFORE:
            case InsertionPosition.AFTER:
              toggle = getPlacedRedoToggle({
                container: container.parentElement,
                widgetConfig,
                anyRedoToggle: groupElementTogether,
              });
              if (toggle) {
                return toggle;
              }
              break;
            case InsertionPosition.PREPEND:
            case InsertionPosition.APPEND:
              toggle = getPlacedRedoToggle({
                container,
                widgetConfig,
                anyRedoToggle: groupElementTogether,
              });
              if (toggle) {
                return toggle;
              }
              break;
            default:
              assertNever(insertionPosition);
              break;
          }
          toggle = document.createElement(
            RedoToggleElement.TAG,
          ) as RedoToggleElement;
          toggle.setToggleClass(widgetConfig?.elementType);
          toggle.id = `redo-toggle-${randomHexString(nativeRandom, 8)}`;
          toggle.setAttribute("tabindex", "0");

          if (widgetConfig) {
            toggle.addEventListener("focus", () => {
              const button = toggle.root?.querySelector("button");
              button?.focus();
            });
          }

          if (autocheck.openShadowRoot) {
            toggle.shadowRootMode = "open";
          }

          if (
            widgetConfig?.elementType ===
              WidgetElementType.COVERAGE_CHECKOUT_BUTTON ||
            widgetConfig?.elementType ===
              WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON
          ) {
            toggle.useShadowRoot = false;
          }
          // Split selectors by comma and check if any of them match the container
          const selectors = onTapcart
            ? TAPCART_SELECTOR
            : widgetConfig?.placementSelector ||
              autocheck.cartToggle.cartToggleSelector ||
              DEFAULT_TOGGLE_SELECTOR;
          const matchingSelector = selectors
            ?.split(",")
            .find((selector) => container.matches(selector));
          toggle.setAttribute("found-selector", matchingSelector || "");

          const onDisconnect = () => {
            log("toggle disconnected");
            toggle.removeEventListener("disconnect", onDisconnect);
            setTimeout(renderToggles, 1000);
          };
          toggle.addEventListener("disconnect", onDisconnect);

          switch (insertionPosition) {
            case InsertionPosition.BEFORE:
              container.before(toggle);
              break;
            case InsertionPosition.AFTER:
              container.after(toggle);
              break;
            case InsertionPosition.PREPEND:
              container.prepend(toggle);
              break;
            case InsertionPosition.APPEND:
              container.append(toggle);
              break;
            default:
              assertNever(insertionPosition);
              break;
          }
          return toggle;
        }),
        widgetConfig,
      });
    }

    setToggles(toggles);
    if (autocheck.dispatchResizeAfterItemsPlaced) {
      window.dispatchEvent(new Event("resize"));
    }
  });

  // attach mutation observer to rerender checkout buttons on dom change
  useEffect(() => {
    if (!autocheck.attachmentStrategy) {
      return;
    }

    log("Create mutation observer");
    const mutationObserver = new MutationObserver((event) => {
      if (!autocheck.noDebounceButtonRendering) {
        setToggleRefresh(Symbol());
        return;
      }

      if (
        coverageInfo?.returns.cartEligible ||
        coverageInfo?.packageProtection.cartEligible
      ) {
        renderToggles();
      } else if (shouldShowBuyNowElements) {
        renderToggles(PlacementType.PDP);
      }
    });

    if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] ||
      globalThis.redoStorefront?.type == "bigcommerce"
    ) {
      mutationObserver.observe(document, { childList: true, subtree: true });
    } else {
      mutationObserver.disconnect();
    }

    return () => {
      mutationObserver.disconnect();
    };
  }, [
    autocheck.attachmentStrategy,
    autocheck.noDebounceButtonRendering,
    renderToggles,
    coverageInfo,
    shouldShowBuyNowElements,
  ]);

  useEventListener(document, RedoListenedDomEvent.ReloadCart, () => {
    setCartRefresh(Symbol());
  });
  useEventListener(document, "rebuy:cart.change", () => {
    setCartRefresh(Symbol());
  });

  const addRedo = useHandler(async (event?: Event, force = false) => {
    if (
      merchantPaid ||
      !coverageInfo ||
      (redoInCart && !force) ||
      redoLock.current === true ||
      !expectedVariantID ||
      autocheck.attachmentStrategy ===
        AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE
    ) {
      return;
    }
    // If redo is in the cart and they are eligible for final sale, then they are paying for final sale.
    // FIXME: This will become a problem when we are splitting the products since finalSaleValue may be greater than 0 and they are not buying final sale coverage.
    const hasFinalSaleCoverage =
      coverageInfo.returns.cartEligible && coverageInfo.returns.isFinalSale;
    try {
      redoLock.current = true;
      setInCart(true);
      const cartParams = new URLSearchParams();
      if (!onTapcart) {
        switch (globalThis?.Shopify?.shop) {
          case "birdiethreads.myshopify.com":
            // cspell:disable-next-line
            cartParams.append("ref_icart", "true");
            break;
          case "thedressoutlet.myshopify.com":
            cartParams.append("sections", "cart-drawer,cart-icon-bubble");
            break;
        }

        if (
          globalThis?.Shopify?.shop === "the-vintage-gentlemen.myshopify.com" &&
          typeof Rebuy !== "undefined"
        ) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          Rebuy.Cart.addItem({
            id: expectedVariantID,
            quantity: 1,
            properties: hasFinalSaleCoverage
              ? { __final_sale_coverage: "true" }
              : {},
          });
          redoLock.current = false;
          return;
        }
      }
      log("adding redo", { expectedVariantID });
      setLocalStorageWithExpiry(REDO_REMOVED_REDO, false);

      event?.preventDefault();
      event?.stopPropagation();

      const itemsToAdd: { items: Partial<ShopifyCartItem>[] } = {
        items: [
          {
            id: expectedVariantID,
            variant_id: expectedVariantID,
            product_id: expectedProductId,
            quantity: 1,
            properties: hasFinalSaleCoverage
              ? { __final_sale_coverage: "true" }
              : {},
          },
        ],
      };

      // Add redo product to bottom of shopify cart if setting is enabled
      if (!onTapcart && autocheck.cartToggle?.cartReplaceItems) {
        await (storefrontClient as ShopifyAjaxClient).cartAddToEnd(
          itemsToAdd,
          cartParams,
        );
      } else {
        await storefrontClient.cartAdd(itemsToAdd, cartParams); // TODO: accept preventReload
      }

      if (globalThis.redoStorefront?.type === "bigcommerce") {
        // If we're on the checkout page for Bigcommerce we need to manually do cart modifications due to lack of a clear cart refresh solution. This may be safe to do on Shopify too, but didn't see a need to.
        setCartRefresh(Symbol());
      }

      await renderCart();

      if (!event) return;

      console.debug(`Resubmitting event ${event.type}`);
      if (
        typeof Rebuy !== "undefined" ||
        globalThis?.Shopify?.shop === "megalashacademy.myshopify.com"
      ) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      if (event.target instanceof HTMLElement && event.type === "click") {
        event.target.click();
      } else {
        event.target!.dispatchEvent(new Event(event.type));
      }
    } catch (e) {
      console.error("Error adding redo to the cart", e);
      // Refresh the cart so we know if it's actually in there or not
      setCartRefresh(Symbol());
    } finally {
      redoLock.current = false;
    }
  });

  const onCheckboxChange = useHandler((value: boolean) => {
    log("checkbox changed", { value });
    checkboxState.current = value;
  });

  const onModalAdd = useHandler(async () => {
    if (!redoInCart) {
      await addRedo();
    }
  });

  const ensurePromise = useRef<Promise<any> | null>(null);
  // Makes sure the correct variant of redo is in the cart.
  useLoad(
    async (signal) => {
      while (ensurePromise.current !== null) {
        try {
          await ensurePromise.current;
        } catch (e) {
          // Who cares
        }
      }

      if (
        !cartChecked ||
        expectedVariantID === null ||
        !checkboxLabelOnly ||
        coverageCheckoutButtonClicked ||
        (globalThis.redoStorefront?.type === "commercecloud" &&
          CommercecloudStorefrontClient.isOnCheckoutPage() &&
          CommercecloudStorefrontClient.isOnPaymentSection())
      ) {
        return;
      }

      let cartChanged = false;
      ensurePromise.current = (async () => {
        log("Ensuring redo", { expectedVariantID });

        if (
          autocheck.attachmentStrategy ===
            AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE &&
          ensurePromiseFeeAction !== EnsurePromiseAction.IGNORE
        ) {
          if (!autocheck.widgetSlug) {
            log("No widget slug, skipping custom fee attachment");
            return;
          }

          const onBigcommerceCheckout =
            globalThis.location?.pathname?.includes("/checkout") &&
            globalThis.redoStorefront?.type !== "commercecloud";

          if (onBigcommerceCheckout) {
            const bigcommerceServerApiClient = new BigcommerceServerApiClient();
            if (
              ensurePromiseFeeAction === EnsurePromiseAction.ADD &&
              redoProductToSell &&
              redoProductToSell.rawCalculatedCoveragePrice
            ) {
              log(
                `Adding Redo custom fee of price ${redoProductToSell.rawCalculatedCoveragePrice}`,
              );
              const attachResponse =
                await bigcommerceServerApiClient.attachCustomFeeToCart({
                  widgetId: autocheck.widgetSlug,
                  checkoutId: (storefrontClient as BigcommerceStorefrontClient)
                    .cartEntityId as string,
                  feeAmount: redoProductToSell.rawCalculatedCoveragePrice,
                  productType: redoProductToSell.productType,
                });
              if (attachResponse.feeUpdated) {
                log("Attached custom fee to cart, refreshing page");
                if (!autocheck.preventPageRefreshAfterCartAdd) {
                  location.reload();
                }
              }
            } else if (ensurePromiseFeeAction === EnsurePromiseAction.REMOVE) {
              log("Removing Redo custom fee");
              const deleteResponse =
                await bigcommerceServerApiClient.deleteCustomFeeFromCart({
                  widgetId: autocheck.widgetSlug,
                  checkoutId: (storefrontClient as BigcommerceStorefrontClient)
                    .cartEntityId as string,
                });
              if (deleteResponse.feeUpdated) {
                log("Deleted custom fee from cart, refreshing page");
                if (!autocheck.preventPageRefreshAfterCartAdd) {
                  location.reload();
                }
              }
            }
          } else {
            log(
              `Not on checkout page, skipping custom fee attachment. Location: ${location.pathname}, calculatedCoveragePrice: ${redoProductToSell?.rawCalculatedCoveragePrice}`,
            );
          }
        }

        // Block checkout while we're making sure the correct variant is in the cart.
        let checkoutIntercepted = false;
        const checkoutInterceptor = autocheck.checkoutSelector
          ? (event: Event) => {
              if (
                event.target instanceof Element &&
                (event.target.matches(autocheck.checkoutSelector!) ||
                  event.target.closest(autocheck.checkoutSelector!))
              ) {
                event.preventDefault();
                event.stopPropagation();
                checkoutIntercepted = true;
                log("Checkout intercepted");
              }
            }
          : null;

        if (checkoutInterceptor) {
          log("Attaching checkout interceptor");
          // Listen on both the main document and the shadow root (if it exists) just in case
          document.addEventListener("click", checkoutInterceptor, {
            capture: true,
          });
          getCartShadowRoot(autocheck)?.addEventListener(
            "click",
            checkoutInterceptor,
            { capture: true },
          );
        }
        try {
          let foundCorrectVariant = false;

          // Fetching the cart from the API instead of using the useCart react state causes issues because they could be inconsistent.
          const _cart =
            globalThis?.Shopify?.shop === "charming-and-main.myshopify.com" ||
            globalThis.redoStorefront?.type === "commercecloud"
              ? cart // For Charming and Main, experiment with using the useCart state instead
              : await storefrontClient.cartGet(signal); // For all other shops, continuing fetching from the API

          for (const item of _cart.items) {
            try {
              if (isRedoItem(item)) {
                // Remove any incorrect variants
                if (item.variant_id !== expectedVariantID) {
                  log("removing incorrect variant", { item });
                  cartChanged = true;
                  await storefrontClient.removeItemFromCart(
                    item,
                    item.quantity,
                    signal,
                    autocheck.preventPageRefreshAfterCartAdd,
                  );
                  if (
                    globalThis.redoStorefront?.type === "bigcommerce" ||
                    globalThis.redoStorefront?.type === "commercecloud"
                  ) {
                    // If we're on the checkout page for Bigcommerce we need to manually do cart modifications due to lack of a clear cart refresh solution. This may be safe to do on Shopify too, but didn't see a need to.
                    setCartRefresh(Symbol());
                  }
                  log("incorrect variant removed");
                  if (
                    (getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) ===
                      "enabled" ||
                      getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) ===
                        "enabled" ||
                      getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) ===
                        "enabled") &&
                    expectedVariantID === 0
                  ) {
                    setLocalStorageWithExpiry(REDO_REMOVED_REDO, true);
                  }
                } else {
                  foundCorrectVariant = true;
                  if (item.quantity > 1) {
                    // If there is more than one, make sure to fix that.
                    log("updating variant quantity", { item });
                    cartChanged = true;
                    await storefrontClient.removeItemFromCart(
                      item,
                      item.quantity - 1,
                      signal,
                    );
                    if (globalThis.redoStorefront?.type === "bigcommerce") {
                      // If we're on the checkout page for Bigcommerce we need to manually do cart modifications due to lack of a clear cart refresh solution. This may be safe to do on Shopify too, but didn't see a need to.
                      setCartRefresh(Symbol());
                    }
                  }
                }
              }
            } catch (e) {
              console.error("Error in ensure product", e);
            }
          }
          if (!foundCorrectVariant && expectedVariantID !== 0) {
            cartChanged = true;
            await addRedo(undefined, true);
          }
          // Graviet uses cartAddToEnd, which when combined with their theme and renderCart,
          // causes a race condition where the cart state is not updated properly and all items are removed.
          if (cartChanged && globalThis?.Shopify?.shop !== "graviet.co") {
            await renderCart();
          }
        } finally {
          // FIXME The checkout selector gets removed before the cart can be refreshed, so sometimes it doesn't block the customer from going to checkout before we update.
          if (checkoutInterceptor) {
            log("Removing checkout interceptor");
            document.removeEventListener("click", checkoutInterceptor, {
              capture: true,
            });
            getCartShadowRoot(autocheck)?.removeEventListener(
              "click",
              checkoutInterceptor,
              { capture: true },
            );

            // If a checkout click was intercepted, click the checkout button again
            // FIXME If there are double toggles, it is possible for a customer to turn them both off, click checkout,
            //   and for us to re-click the checkout button while the second toggle update is still processing, which
            //   will take the customer to the checkout page with one Redo product still in the cart.
            if (checkoutIntercepted) {
              // Wait for 500ms for the cart to get re-rendered first
              await new Promise((resolve) => setTimeout(resolve, 500));

              log("Re-clicking checkout button");

              const checkoutElements = queryDocumentAndShadowRoot(
                autocheck.checkoutSelector!,
                autocheck,
              );
              // Just in case there are multiple checkout buttons that match the selector,
              // find the first one that we can click on.
              for (const element of checkoutElements) {
                if (element instanceof HTMLElement) {
                  element.click();
                  break;
                }
              }
            }
          }
        }
      })();

      try {
        document.body.dispatchEvent(new CustomEvent("redo:cart-check:start"));
        return await ensurePromise.current;
      } catch (e) {
        log("Error in ensureRedoLoad", e);
      } finally {
        document.body.dispatchEvent(new CustomEvent("redo:cart-check:end"));
        log("Finished ensuring redo", { expectedVariantID });
        ensurePromise.current = null;
      }
    },
    [
      expectedVariantID,
      ensurePromiseFeeAction,
      cartItems,
      coverageCheckoutButtonClicked,
      redoProductToSell,
    ],
  );

  // Check current cart and render toggles.
  const cartCheckLoad = useLoad(
    async (signal) => {
      if (!fees || !mainRedoProduct || !cart || countryLoadPending) {
        log("skipping cart check", { fees, mainRedoProduct, cart });
        // Set REDO_REMOVED_REDO to true when cart is empty
        setLocalStorageWithExpiry(REDO_REMOVED_REDO, true);
        return;
      }

      setCartCurrency(isCurrency(cart.currency) ? cart.currency : Currency.USD);
      log("checking cart items", { cart });
      // Check if the cart has actually changed.
      let inCart = !!redoInCart;
      const inCartLastCheck = redoInCart === undefined ? true : !!redoInCart;
      const redoItems: ShopifyCartItem[] = [];
      const hasPackageAndSplit =
        autocheck.packageProtection.coverageEnabled &&
        autocheck.packageProtection.splitProducts;
      // Checks if cart line items have changed or if any selling plans have changed.

      // Set these to false until we actually check the cart.
      inCart = false;
      let hasValidReturnProduct = false;
      let hasInvalidReturnProduct = false;
      let numberValidReturnProducts = 0;
      let cartValueReturns = 0;
      let cartValuePackageProtection = 0;
      let finalSaleReturnsCartValue = 0;
      let hasValidProtectionProduct = false;
      let hasValidFinalSaleReturnsProduct = false;
      let hasInvalidProtectionProduct = false;
      const validCartItems = [];

      if (cart?.items?.length) {
        for (const item of cart.items) {
          if (isRedoItem(item)) {
            inCart = true;
            redoItems.push(item);
            continue;
          }

          // don't skip if we have this setting or if we're using dynamic pricing
          if (
            !autocheck.excludeCoverageIfAnyInvalidProduct &&
            !autocheck.dynamicReturnsPricing && // No need to check product tags if we already have one valid product.
            // Unless we have package protection or final sale coverage enabled

            ((hasValidReturnProduct && hasValidProtectionProduct) ||
              !item.handle) &&
            !autocheck.packageProtection.coverageEnabled &&
            !autocheck.finalSaleReturns.coverageEnabled
          ) {
            continue;
          }

          // Don't offer coverage for digital products
          if (!item.requires_shipping) {
            continue;
          }

          // Don't offer coverage for items with zero quantity
          if (item.quantity <= 0) {
            continue;
          }

          const itemValidForOfferings = await itemValidForReturnsFrontend({
            autocheck,
            item,
            redoShopifyExtensionClient,
            shopifyClient: storefrontClient,
            signal,
            invalidReturnProductTags,
            invalidProtectionProductTags,
          });

          if (itemValidForOfferings.returnsValid) {
            cartValueReturns += item.line_price || 0;
            numberValidReturnProducts += item.quantity;
            hasValidReturnProduct = true;
          } else {
            hasInvalidReturnProduct = !itemValidForOfferings.returnsValid;
          }

          if (itemValidForOfferings.protectionValid) {
            cartValuePackageProtection += item.line_price || 0;
            hasValidProtectionProduct = true;
          } else {
            hasInvalidProtectionProduct =
              !itemValidForOfferings.protectionValid;
          }

          if (
            itemValidForOfferings.finalSaleReturnsValid &&
            autocheck.finalSaleReturns.coverageEnabled
          ) {
            finalSaleReturnsCartValue += item.line_price || 0;
            hasValidFinalSaleReturnsProduct = true;
          }
          if (
            itemValidForOfferings.returnsValid ||
            itemValidForOfferings.protectionValid ||
            itemValidForOfferings.finalSaleReturnsValid
          ) {
            validCartItems.push(item);
          }
        }

        const cartTotal = onTapcart
          ? Tapcart!.variables?.cart?.subtotal
          : cart.items.reduce((acc, item) => acc + (item.line_price || 0), 0) /
            100;

        if (
          autocheck.packageProtection.minCoverageEligibleCartTotal &&
          cartTotal < autocheck.packageProtection.minCoverageEligibleCartTotal
        ) {
          hasValidProtectionProduct = false;
        } else if (
          autocheck.packageProtection.maxCoverageEligibleCartTotal &&
          cartTotal > autocheck.packageProtection.maxCoverageEligibleCartTotal
        ) {
          hasValidProtectionProduct = false;
        }

        if (autocheck.excludeCoverageIfAnyInvalidProduct) {
          hasValidReturnProduct =
            hasValidReturnProduct && !hasInvalidReturnProduct;
          hasValidProtectionProduct =
            hasValidProtectionProduct && !hasInvalidProtectionProduct;
          if (hasInvalidReturnProduct) {
            log(
              "cart ineligible for returns because there's an invalid product in the cart",
              {
                excludeCoverageIfAnyInvalidProduct:
                  autocheck.excludeCoverageIfAnyInvalidProduct,
              },
            );
          }
        }
      }
      const sellingPlanPromises = validCartItems
        .filter((item) => !isRedoItem(item))
        .filter((item) => item.selling_plan_allocation?.selling_plan?.id)
        .map(
          async (item) =>
            await getSellingPlan(redoShopifyExtensionClient, {
              sellingPlanId: item.selling_plan_allocation!.selling_plan!.id,
            }),
        );

      const sellingPlans = await Promise.all(sellingPlanPromises);
      setCartSellingPlans(sellingPlans);

      log("cart items checked", {
        inCart,
        inCartLastCheck,
        redoItems,
        hasValidReturnProduct,
        hasValidProtectionProduct,
        hasValidFinalSaleReturnsProduct,
      });
      if (!redoItems.length) {
        setLocalStorageWithExpiry(SELLING_PLANS_ID, {});
      }

      if (inCart && redoItems.length) {
        let localStorageSellingPlans: any =
          getLocalStorageWithExpiry(SELLING_PLANS_ID) || {};

        for (const redoItem of redoItems) {
          // find the same item in fees
          const feesItem = [
            fees.returnProduct,
            fees.packageProduct,
            fees.bothProduct,
          ].find((e) => e?.productId === redoItem.product_id);

          if (!feesItem) {
            continue;
          }

          const sellingPlanId = sellingPlans?.length
            ? getMatchingRedoSellingPlanId(feesItem, sellingPlans)
            : null;

          const redoHasSellingPlan =
            !!redoItem?.selling_plan_allocation?.selling_plan?.id;
          const redoRemovedSellingPlan =
            localStorageSellingPlans[redoItem.product_id] === "removed";

          // If the customer removes the selling plan, we will skip updates
          if (
            sellingPlanId &&
            redoHasSellingPlan &&
            !redoRemovedSellingPlan &&
            localStorageSellingPlans[redoItem.product_id]
          ) {
            continue;
          }
          // If there is a selling plan for the item, add it to redo and add it to local storage
          // If the customer updates their selling plan, we will update the redo item and the local storage
          if (sellingPlanId) {
            localStorageSellingPlans = {
              ...localStorageSellingPlans,
              [redoItem.product_id]: sellingPlanId,
            };

            setLocalStorageWithExpiry(
              SELLING_PLANS_ID,
              localStorageSellingPlans,
            );

            log("updating selling plan", { redoItem, sellingPlanId });
            await storefrontClient.updateCartItemSellingPlan(
              redoItem,
              sellingPlanId,
            );
            // If there is no selling plan for the item but redo has a selling plan, remove it from redo and local storage
          } else if (redoHasSellingPlan) {
            log("removing selling plan", { redoItem });
            await storefrontClient.updateCartItemSellingPlan(redoItem, null);
          }

          if (!sellingPlanId && localStorageSellingPlans[redoItem.product_id]) {
            localStorageSellingPlans = {
              ...localStorageSellingPlans,
              [redoItem.product_id]: "removed",
            };

            setLocalStorageWithExpiry(
              SELLING_PLANS_ID,
              localStorageSellingPlans,
            );
          }
        }
      }
      const returnState =
        getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) === "enabled";
      const protectionState =
        getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) === "enabled";
      const singleState =
        getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) === "enabled";

      let redoForciblyRemoved = false;
      let hasEligibleProduct = false;

      const isZapietPickup = cart.attributes?.["Checkout-Method"] === "pickup";

      const isBlockingBusinessAccount =
        autocheck.disableBusinessAccountInStorefront &&
        !!buyerIdentityLoad.value?.purchasingCompany?.company;

      const validShippingMethod =
        storefrontClient.type !== ShopifyClientType.COMMERCE_CLOUD ||
        cart.delivery_type !== DeliveryType.IN_STORE_PICKUP;

      log(`Valid shipping method: ${validShippingMethod}`, {
        clientType: storefrontClient.type,
        isDeliveryToStore: cart.delivery_type,
      });

      const returnsEligibleAndCovered =
        hasValidReturnProduct &&
        autocheck.coverageEnabled &&
        validCountryReturns &&
        !isZapietPickup &&
        !isBlockingBusinessAccount &&
        validShippingMethod;
      const packageProtectionEligibleAndCovered =
        autocheck.packageProtection?.coverageEnabled &&
        hasValidProtectionProduct &&
        validCountryPackageProtection &&
        !isZapietPickup &&
        !isBlockingBusinessAccount &&
        validShippingMethod;
      const finalSaleReturnsEligibleAndCovered =
        hasValidFinalSaleReturnsProduct &&
        autocheck.finalSaleReturns.coverageEnabled &&
        validCountryFinalSale &&
        !isZapietPickup &&
        !isBlockingBusinessAccount &&
        validShippingMethod;

      if (
        !autocheck.packageProtection?.splitProducts &&
        (autocheck.coverageEnabled ||
          autocheck.finalSaleReturns?.coverageEnabled) &&
        autocheck.packageProtection?.coverageEnabled
      ) {
        hasEligibleProduct =
          (returnsEligibleAndCovered || finalSaleReturnsEligibleAndCovered) &&
          packageProtectionEligibleAndCovered;
      } else {
        hasEligibleProduct =
          returnsEligibleAndCovered ||
          finalSaleReturnsEligibleAndCovered ||
          packageProtectionEligibleAndCovered;
      }

      // This check ALWAYS needs to happen AFTER async functions and BEFORE state is updated
      if (signal.aborted) {
        return;
      }

      // Make sure redo isn't in cart if no eligible products are in cart
      if (!hasEligibleProduct) {
        setSingleToggleVal(false);
        setReturnToggleVal(false);
        setProtectionToggleVal(false);
      }

      // Report timing for coverage eligibility check
      reportTiming("coverage.eligibilityChecked");

      const redoWasRemoved = !inCart && hasEligibleProduct && inCartLastCheck;
      const redoWasRemovedByRedo: boolean =
        getLocalStorageWithExpiry(REDO_REMOVED_REDO);
      if (hasPackageAndSplit && hasTwoOptions) {
        if ((returnState || protectionState) && redoWasRemoved) {
          redoForciblyRemoved = true;
        }
      } else if (singleState && redoWasRemoved && !redoWasRemovedByRedo) {
        redoForciblyRemoved = true;
      }

      // If redo was removed by customer set all to false
      if (redoForciblyRemoved && !ensurePromise.current) {
        log("redo was forcibly removed or disabled from cart attribute");
        if (hasPackageAndSplit && hasTwoOptions) {
          if (returnToggleState) {
            setReturnToggleVal(false);
            setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, "disabled");
          }
          if (protectionToggleState) {
            setProtectionToggleVal(false);
            setLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY, "disabled");
          }
        } else if (singleToggleState) {
          setSingleToggleVal(false);
          setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, "disabled");
        }
      }

      const returnDisabled =
        getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) === "disabled";
      const protectionDisabled =
        getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) === "disabled";
      const singleDisabled =
        getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) === "disabled";

      const basePriceFromProduct = returnRedoProduct?.price_min ?? 0;
      const basePriceFromProductDollars = onTapcart
        ? basePriceFromProduct
        : basePriceFromProduct / 100;
      const basePriceDollars =
        basePriceFromProductDollars ||
        (autocheck.pricePerOrder ? parseFloat(autocheck.pricePerOrder) : 0);

      const coveragePrices = getCalculatedCoveragePrice({
        basePriceDollars,
        autocheck,
        onTapcart,
        cartValueReturnableItems: cartValueReturns,
        cartValuePackageProtectionItems: cartValuePackageProtection,
        finalSaleReturnsCartValue,
        cartItems,
        numValidReturnProducts: numberValidReturnProducts,
        country: countryLoad.value,
        cartHasSubscriptionItem: validCartItems.some(
          (item) => item.selling_plan_allocation?.selling_plan?.id,
        ),
      });

      const returnsCartEligible =
        returnsEligibleAndCovered || finalSaleReturnsEligibleAndCovered;

      const coverageInfo = {
        returns: {
          cartEligible: returnsCartEligible,
          price: returnsCartEligible ? coveragePrices.returnCost : 0,
          isFinalSale: finalSaleReturnsEligibleAndCovered,
        },
        packageProtection: {
          cartEligible: packageProtectionEligibleAndCovered,
          price: packageProtectionEligibleAndCovered
            ? coveragePrices.packageProtectionCost
            : 0,
        },
        both: {
          cartEligible:
            returnsCartEligible && packageProtectionEligibleAndCovered,
          price:
            returnsCartEligible && packageProtectionEligibleAndCovered
              ? coveragePrices.bothCost
              : 0,
        },
      };

      const coverageInfoFromCoverageCalculators =
        await getCoverageInfoFromCoverageCalculators({
          autocheck,
          customerInfo: {
            country: countryLoad.value || "",
            customerTags: [], // invalid customer tags are handled through autocheck currently
            isBusinessAccount:
              !!buyerIdentityLoad.value?.purchasingCompany?.company,
            usingZapietPickup: isZapietPickup,
          },
          cart,
          shopifyClient: storefrontClient,
          redoClient: redoShopifyExtensionClient,
          onTapcart,
        });

      setCoverageInfo({
        returns: {
          ...coverageInfo.returns,
          reasonIneligible:
            coverageInfoFromCoverageCalculators.returns.reasonIneligible,
        },
        packageProtection: {
          ...coverageInfo.packageProtection,
          reasonIneligible:
            coverageInfoFromCoverageCalculators.packageProtection
              .reasonIneligible,
        },
        both: {
          ...coverageInfo.both,
          reasonIneligible:
            coverageInfoFromCoverageCalculators.both.reasonIneligible,
        },
      });

      compareCoverageInfos({
        oldCoverageInfo: coverageInfo,
        newCoverageInfo: coverageInfoFromCoverageCalculators,
        autocheck,
        cart,
      });
      const bothInCart =
        fees.bothProduct?.productId === redoItems[0]?.product_id;
      const returnInCart =
        bothInCart ||
        fees.returnProduct?.productId === redoItems[0]?.product_id;
      const packageInCart =
        bothInCart ||
        fees.packageProduct?.productId === redoItems[0]?.product_id;

      if (
        cartEnabled &&
        !redoForciblyRemoved &&
        !isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy]
      ) {
        // If cart is eligible for adding a redo item, add it by changing toggles
        const someCoverageEnabled =
          autocheck.coverageEnabled ||
          !!autocheck.finalSaleReturns?.coverageEnabled;
        if (hasPackageAndSplit && someCoverageEnabled && hasTwoOptions) {
          if (
            (hasValidReturnProduct || hasValidFinalSaleReturnsProduct) &&
            !returnToggleState &&
            (returnInCart || (autocheck.redoAutoCheck && !returnDisabled))
          ) {
            setReturnToggleVal(true);
            setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, "enabled");
          }
          if (
            hasValidProtectionProduct &&
            !protectionToggleState &&
            (packageInCart ||
              (autocheck.packageProtectionAutoCheck && !protectionDisabled))
          ) {
            setProtectionToggleVal(true);
            setLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY, "enabled");
          }
        } else if (
          hasEligibleProduct &&
          !singleToggleState &&
          (inCart || (autocheck.redoAutoCheck && !singleDisabled))
        ) {
          setSingleToggleVal(true);
          setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, "enabled");
        }
      }

      setInCart(inCart);
      setCheckedCart(cart);

      if (cart?.items?.length) {
        // Check if any item has coverage
        const hasAnyCoverage = await Promise.any(
          cart.items.map(async (item) => {
            const itemValidForOfferings = await itemValidForReturnsFrontend({
              autocheck,
              item,
              redoShopifyExtensionClient,
              shopifyClient: storefrontClient,
              signal,
              invalidReturnProductTags,
              invalidProtectionProductTags,
            });
            return (
              itemValidForOfferings.returnsValid ||
              itemValidForOfferings.protectionValid ||
              itemValidForOfferings.finalSaleReturnsValid
            );
          }),
        );

        // Set REDO_REMOVED_REDO based on coverage status
        setLocalStorageWithExpiry(REDO_REMOVED_REDO, !hasAnyCoverage);
      } else {
        // Set to true if cart is empty
        setLocalStorageWithExpiry(REDO_REMOVED_REDO, true);
      }
    },
    [
      buyerIdentityLoad,
      cartItems,
      fees,
      countryLoadPending,
      validCountryReturns,
      validCountryPackageProtection,
      mainRedoProduct,
    ],
  );

  useEffect(() => {
    if (cartCheckLoad.error) {
      log("error checking cart", { error: cartCheckLoad.error });
    }
  }, [cartCheckLoad.error]);

  const applyCartCountModificationsTrigger = useTriggerLoad(
    async (signal?: AbortSignal) => {
      if (!autocheck.hideRedoProduct?.enabled || !cart || !cartItems?.length) {
        return;
      }
      let redoProductPrice = 0;
      if (redoProductToSell) {
        redoProductPrice = redoProductToSell.price;
      }

      await applyCartCountModificationsWithRetries({
        autocheck,
        cart,
        redoProductPrice,
        redoShouldBeInCart: !!expectedVariantID,
        signal,
      });
    },
  )[1];

  const abortControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!autocheck.hideRedoProduct?.enabled || !cart || !cartItems?.length) {
      return;
    }

    // Cancel the previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    applyCartCountModificationsTrigger(abortController.signal);

    // Cleanup: abort on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };

    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cartItems,
    cartRefresh,
    cart?.total_price,
    singleToggleState,
    cart,
    expectedVariantID,
  ]);

  // Listen for click events and refresh toggles.
  useEventListener(
    document,
    "click",
    (e: Event) => {
      if (
        e.target instanceof Element &&
        // Check if the click was one of the elements that could trigger a cart change or open.
        ((autocheck.cartToggle.cartClickListenerSelector &&
          e.target.matches(autocheck.cartToggle.cartClickListenerSelector)) ||
          e.target.matches(DEFAULT_TOGGLE_CLICK_SELECTOR))
      ) {
        for (let i = 1; i <= 5; i *= 2) {
          setTimeout(() => setCartRefresh(Symbol()), i * 500);
        }
      }
    },
    true,
  );

  const pageChange = usePageChange();
  // This will trigger the checkbox to be added if it needs to be, as well as re-rendering the toggle.
  useEffect(() => {
    if (!pageChange) {
      return;
    }
    setCheckboxRefresh(pageChange);
    setCartRefresh(pageChange);
  }, [pageChange]);

  // Add checkbox if on product page
  useEffect(() => {
    if (document.querySelector("redo-checkbox") || isOnProductPage(onTapcart)) {
      setCheckbox(addCheckbox(autocheck, onTapcart));
    } else if (checkbox) {
      setCheckbox(null);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkboxRefresh]);

  /**
   * pdpProductLoad is used for the PDP checkbox and buy now button. It detects if we are currently on a product page and if the product is valid for the coverage.
   */
  const pdpProductLoad = useLoad(
    async (signal) => {
      const handle = onTapcart
        ? Tapcart!.variables?.product?.handle
        : shopifyProductHandle(location.href);

      if (!handle) {
        return {
          isValidProduct: false,
          returnsValid: false,
          protectionValid: false,
          finalSaleReturnsValid: false,
          product: undefined,
        };
      }

      let product: ShopifyProduct;
      try {
        product = await storefrontClient.productGet(handle, signal);
      } catch (e) {
        log(
          "Error fetching product, likely because we're not on a product's page",
          { handle, error: e },
        );
        return {
          isValidProduct: false,
          returnsValid: false,
          protectionValid: false,
          finalSaleReturnsValid: false,
          product: undefined,
        };
      }

      const eligibility = await itemValidForReturnsFrontend({
        autocheck,
        item: { product_id: product.id, handle },
        redoShopifyExtensionClient,
        shopifyClient: storefrontClient,
        signal,
        invalidReturnProductTags,
        invalidProtectionProductTags,
        shopifyProduct: product,
      });
      log("pdp eligibility", eligibility);

      const hasBothCoverage =
        (autocheck.coverageEnabled ||
          autocheck.finalSaleReturns?.coverageEnabled) &&
        autocheck.packageProtection?.coverageEnabled;

      let isValidProduct = false;

      if (hasBothCoverage && !autocheck.packageProtection?.splitProducts) {
        isValidProduct =
          (eligibility.returnsValid || eligibility.finalSaleReturnsValid) &&
          eligibility.protectionValid;
      } else {
        isValidProduct =
          eligibility.returnsValid ||
          eligibility.protectionValid ||
          eligibility.finalSaleReturnsValid;
      }

      if (
        (checkbox &&
          (document.querySelector("redo-checkbox") ||
            isOnProductPage(onTapcart))) ||
        autocheck.useBuyNowCoverage
      ) {
        return { isValidProduct, ...eligibility, product };
      }

      return { isValidProduct: false, ...eligibility, product };
    },
    [
      checkbox,
      cart?.items?.length,
      onTapcart,
      autocheck.coverageExcludeCollections,
      autocheck.finalSaleReturns.coverageEnabled,
      autocheck.finalSaleReturns.coverageValidTags,
      autocheck.finalSaleReturns.coverageValidCollections,
      invalidReturnProductTags,
    ],
  );

  useEffect(
    () => {
      const variant = extractProductFormData();
      if (
        variant &&
        (variant.variantId !== pdpSelectedVariant?.variantId ||
          variant.quantity !== pdpSelectedVariant?.quantity)
      ) {
        setPdpSelectedVariant(variant);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pdpProductLoadRef.current, toggleRefresh],
  );

  useEffect(() => {
    if (pdpProductLoad.error) {
      console.error(pdpProductLoad.error);
    }
  }, [pdpProductLoad.error]);

  // Update ref so mutation observer can use validProductLoad value early
  useEffect(() => {
    pdpProductLoadRef.current = pdpProductLoad.value;
  }, [pdpProductLoad.value, toggleRefresh]);

  // This is used to determine the correct redo product to add to cart from pdp buy now buttons
  useEffect(
    () => {
      if (
        !mainRedoProduct ||
        !fees ||
        !autocheck.enabled ||
        !pdpProductLoadRef.current?.isValidProduct ||
        !pdpSelectedVariant?.variantId
      ) {
        return undefined;
      }

      const shopifyVariant = pdpProductLoadRef.current.product?.variants.find(
        (variant) =>
          String(variant.id) === String(pdpSelectedVariant.variantId),
      );

      if (!shopifyVariant) {
        return undefined;
      }

      // NOT compatible with Tapcart
      const price = shopifyVariant.price * 100;
      const coveragePrices = getCalculatedCoveragePrice({
        basePriceDollars: autocheck.pricePerOrder
          ? parseFloat(autocheck.pricePerOrder)
          : 0,
        autocheck,
        onTapcart,
        cartValueReturnableItems: pdpProductLoadRef.current.returnsValid
          ? price
          : 0,
        cartValuePackageProtectionItems: pdpProductLoadRef.current
          .protectionValid
          ? price
          : 0,
        finalSaleReturnsCartValue: pdpProductLoadRef.current
          .finalSaleReturnsValid
          ? price
          : 0,
        cartItems,
        numValidReturnProducts: pdpProductLoadRef.current.returnsValid
          ? pdpSelectedVariant.quantity
          : 0,
        country: countryLoad.value,
        cartHasSubscriptionItem: false,
      });

      const pdpCoverageInfo = {
        returns: {
          cartEligible: pdpProductLoadRef.current.returnsValid,
          price: coveragePrices.returnCost,
          isFinalSale: pdpProductLoadRef.current.finalSaleReturnsValid,
        },
        packageProtection: {
          cartEligible: pdpProductLoadRef.current.protectionValid,
          price: coveragePrices.packageProtectionCost,
        },
        both: {
          cartEligible:
            pdpProductLoadRef.current.returnsValid &&
            pdpProductLoadRef.current.protectionValid,
          price: coveragePrices.bothCost,
        },
      };

      const singleProduct = determineRedoProduct({
        mainRedoProduct,
        returnRedoProduct,
        packageRedoProduct,
        bothRedoProduct,
        fees,
        autocheck,
        coverageInfo: pdpCoverageInfo,
        toggleStates: { returnToggleState, protectionToggleState },
      });

      const singleProductVariant = getCorrectlyPricedVariant({
        product: singleProduct,
        productType: productTypeMap.get(singleProduct.id),
        coverageInfo: pdpCoverageInfo,
        country: countryLoad.value ?? undefined,
      });

      log("pdp buy now selected redo product", singleProductVariant);
      setBuyNowRedoProduct(singleProductVariant.id.toString());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pdpSelectedVariant,
      fees,
      autocheck,
      cartItems,
      invalidReturnProductTags,
      invalidProtectionProductTags,
      coverageCheckoutButtonClicked,
      setCoverageCheckoutButtonClicked,
    ],
  );

  useEffect(() => {
    if (
      coverageInfo?.returns.cartEligible ||
      coverageInfo?.packageProtection.cartEligible
    ) {
      log("Eligible for returns or package protection.");
      renderToggles();
    } else if (shouldShowBuyNowElements) {
      log("Eligible for the product page.");
      renderToggles(PlacementType.PDP);
    } else {
      log(
        `Not eligible for any products: ${coverageInfo?.returns.reasonIneligible ? `${coverageInfo.returns.reasonIneligible}.` : ""} ${coverageInfo?.packageProtection.reasonIneligible ? `${coverageInfo.packageProtection.reasonIneligible}` : ""}`,
      );
    }
  }, [
    cart,
    cartRefresh,
    toggleRefresh,
    renderToggles,
    coverageInfo,
    shouldShowBuyNowElements,
  ]);

  const showCheckbox =
    (checkboxLabelOnly || !redoInCart) &&
    pdpProductLoad.value?.isValidProduct &&
    (cartCheckedOnce || merchantPaid) &&
    validCountryReturns;
  // Add checkbox event handlers
  useEffect(() => {
    if (!checkbox || !showCheckbox || checkboxLabelOnly) {
      return;
    }

    // Boolean to make sure we only add redo once for these handlers.
    // Once we add it, they will all be removed on the next re-render
    // because showCheckbox will be false.
    let added = false;
    const form = checkbox.closest("form");
    async function formEventHandler(e: SubmitEvent) {
      if (checkboxState.current && !added) {
        added = true;
        await addRedo(e);
      }
    }
    async function rebuyHandler(e: any): Promise<void> {
      if (checkboxState.current && !added) {
        added = true;
        // Don't add redo when we're already adding redo.
        if (String(e?.detail?.item?.variant_id) === String(fees?.variantId)) {
          return;
        }
        await addRedo();
      }
    }
    async function clickEventHandler(e: MouseEvent): Promise<void> {
      if (
        e.target instanceof Element &&
        globalThis?.Shopify?.theme?.name === "Dawn" &&
        e.target.closest(".shopify-payment-button__button")
      ) {
        // Buy it now button for the dawn theme
        const quantityElement = document.querySelector(
          '.quantity__input, input[type="number"]',
        ) as HTMLInputElement | null;
        const quantity = quantityElement?.value || "1";
        const currentSelectedVariant = (
          document.querySelector(
            ".product__pickup-availabilities",
          ) as HTMLElement
        ).dataset.variantId;
        e.preventDefault();
        e.stopPropagation();
        location.href = `https://${
          globalThis?.Shopify.shop
        }/cart/${currentSelectedVariant}:${quantity},${
          fees!.variantId
        }:1?traffic_source=buy_now`;
      } else if (
        e.target instanceof Element &&
        e.target.closest(getSubmitButtonSelector(autocheck)) &&
        checkboxState.current &&
        !added
      ) {
        added = true;
        await addRedo(e);
      }
    }

    form?.addEventListener("submit", formEventHandler);
    document.addEventListener("rebuy:cart.add", rebuyHandler);
    document.addEventListener("click", clickEventHandler, true);

    return () => {
      form?.removeEventListener("submit", formEventHandler);
      document.removeEventListener("rebuy:cart.add", rebuyHandler);
      document.removeEventListener("click", clickEventHandler);
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkbox, showCheckbox, cartEnabled]);

  if (!fees) {
    return null;
  }

  // In preview mode, bypass certain restrictions
  const isPreviewMode = (globalThis as any).redoMode === "preview";

  if (
    !isPreviewMode &&
    !merchantPaid &&
    (autocheck.disableRedoForCustomer ||
      (autocheck.hideCheckbox && !autocheck.cartToggle.enabled && !onTapcart) ||
      !autocheck.enabled ||
      (autocheck.hideCheckbox && !cartCheckedOnce))
  ) {
    log("returning empty", {
      merchantPaid,
      disableRedoForCustomer: autocheck.disableRedoForCustomer,
      hideCheckbox: autocheck.hideCheckbox,
      cartToggleEnabled: autocheck.cartToggle.enabled,
      enabled: autocheck.enabled,
      cartChecked,
    });
    return null;
  }

  let showToggles = false;
  const hasBothCoverage =
    (autocheck.coverageEnabled ||
      autocheck.finalSaleReturns?.coverageEnabled) &&
    autocheck.packageProtection?.coverageEnabled;

  if (hasBothCoverage && !autocheck.packageProtection?.splitProducts) {
    showToggles = !!(
      coverageInfo?.returns.cartEligible &&
      coverageInfo?.packageProtection.cartEligible
    );
  } else {
    showToggles = !!(
      coverageInfo?.returns.cartEligible ||
      coverageInfo?.packageProtection.cartEligible
    );
  }

  if (!showToggles && coverageInfo) {
    log(
      `cart is not eligible: ${coverageInfo.returns.reasonIneligible ? `${coverageInfo.returns.reasonIneligible}.` : ""} ${coverageInfo.packageProtection.reasonIneligible ? `${coverageInfo.packageProtection.reasonIneligible}` : ""}`,
    );
  }

  if (
    isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
    autocheck.hideRedoProduct
  ) {
    autocheck.hideRedoProduct.enabled = showToggles;
  }

  if (shouldShowBuyNowElements) {
    showToggles = true;
  }
  const classicWidgetTheme = getClassicWidgetTheme({ autocheck });
  return (
    <>
      {checkbox && showCheckbox ? (
        <StyleShadowRoot
          shadowRoot={checkbox.root}
          styleManager={STYLE_MANAGER}
        >
          <ModalRootContext.Provider value={checkbox.modalContainer}>
            <RedoCheckbox
              checkboxText={autocheck.checkboxText}
              coverage={autocheck.coverage}
              hideCheckbox={onTapcart ? false : autocheck.hideCheckbox}
              hidePrice={autocheck.hideCheckboxPrice}
              infoLink={autocheck.infoIconLink}
              initial={autocheck.redoAutoCheck}
              labelOnly={checkboxLabelOnly}
              modalLogo={autocheck.modalLogo}
              modalTextAdjustments={autocheck.modalTextAdjustments}
              onAdd={onModalAdd}
              onChange={onCheckboxChange}
              packageProtectionEnabled={
                autocheck.packageProtection.coverageEnabled
              }
              packageProtectionPlusEnabled={
                autocheck.packageProtection.packageProtectionPlusEnabled
              }
              paymentModel={autocheck.paidModel}
              price={
                !autocheck.packageProtection.coverageEnabled
                  ? {
                      amount: redoProductToSell?.price || +fees.price,
                      currency: cartCurrency,
                    }
                  : undefined
              }
              showInfo={
                !onTapcart &&
                (autocheck.coverageEnabled ||
                  autocheck.finalSaleReturns?.coverageEnabled)
              }
              theme={classicWidgetTheme}
            />
          </ModalRootContext.Provider>
        </StyleShadowRoot>
      ) : null}

      {isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
        coverageInfo &&
        showToggles &&
        redoProductToSell &&
        productIdToSellingPlanId.value && (
          <WidgetManager
            autocheck={autocheck}
            cart={cart}
            cartCurrency={cartCurrency}
            coverageCheckoutButtonClicked={coverageCheckoutButtonClicked}
            coverageInfo={coverageInfo}
            fees={fees}
            productIdToSellingPlanId={productIdToSellingPlanId.value}
            redoClient={redoShopifyExtensionClient}
            redoProductToSell={redoProductToSell}
            setCoverageCheckoutButtonClicked={setCoverageCheckoutButtonClicked}
            setOnBuyNowCoverage={setOnBuyNowCoverage}
            shopifyClient={
              shouldUseStorefrontGraphqlClientForRIC
                ? (shopifyStorefrontGraphqlClient ?? storefrontClient)
                : storefrontClient
            }
            toggles={toggles}
            triggerCartRefresh={() => setCartRefresh(Symbol())}
          />
        )}

      {showToggles &&
        redoProductToSell &&
        coverageInfo &&
        !(!!onTapcart && isOnProductPage(onTapcart)) &&
        singleCartToggleAttachmentStrategies.includes(
          autocheck.attachmentStrategy,
        ) && (
          <SingleToggleManager
            autocheck={autocheck}
            cartCurrency={cartCurrency}
            coverageInfo={coverageInfo}
            fees={fees}
            redoProductToSell={redoProductToSell}
            setSingleToggleVal={setSingleToggleVal}
            singleToggleState={singleToggleState}
            toggles={toggles}
          />
        )}

      {showToggles &&
        coverageInfo &&
        autocheck.attachmentStrategy === AttachmentStrategy.DOUBLE_TOGGLE && (
          <DoubleToggleManager
            autocheck={autocheck}
            cartCurrency={cartCurrency}
            country={countryLoad.value ?? ""}
            coverageInfo={coverageInfo}
            fees={fees}
            onTapcart={onTapcart}
            packageRedoProduct={packageRedoProduct}
            protectionToggleState={protectionToggleState}
            returnRedoProduct={returnRedoProduct}
            returnToggleState={returnToggleState}
            setProtectionToggleVal={setProtectionToggleVal}
            setReturnToggleVal={setReturnToggleVal}
            toggles={toggles}
          />
        )}
    </>
  );
});
